"use client";

/* eslint-disable react-hooks/purity */
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/Button";
import { toast } from "@/components/ui/toast";
import { userApi, invalidateCache } from "@/lib/api-client";
import { SessionManager } from "@/components/account/SessionManager";
import { formatDate } from "@/lib/utils";
import { Star, Calendar, Zap, ShieldAlert, Eye, EyeOff } from "lucide-react";
import { useTenant, useTenantFeatures, useMonetizationMode } from "@/components/tenant/TenantProvider";
import { PageHeader } from "@/components/ui/dashboard-chrome";
import { DicebearCustomizer } from "@/components/ui/dicebear-customizer";
import { inputClassUser as inputClass } from "@/lib/form-classes";
interface UserProfile {
  id: string;
  name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  role: string;
}
interface Membership {
  id: string;
  status: string;
  starts_at: string;
  expires_at?: string;
  plan_name: string;
  duration_type: string;
  plan_slug: string;
}
export default function AccountPage() {
  const {
    brand_name
  } = useTenant();
  const features = useTenantFeatures();
  const monetizationMode = useMonetizationMode();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string>("");
  const [bio, setBio] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [membership, setMembership] = useState<Membership | null>(null);
  useEffect(() => {
    Promise.all([fetch(`/api/auth/me`, {
      credentials: "include"
    }).then(r => r.json()).then(data => {
      if (data.success) {
        setUser(data.data.user);
        setName(data.data.user.name);
        setPhone(data.data.user.phone || "");
        setAvatarUrl(data.data.user.avatar_url || "");
      }
    }), userApi.profile().then(res => {
      if (res.success && res.data) {
        setBio(res.data.bio || "");
      }
    }), userApi.membership().then(res_0 => {
      if (res_0.success) {
        setMembership(res_0.data);
      }
    })]).catch(() => {}).finally(() => setInitialLoading(false));
  }, []);
  const saveProfile = async (body: Record<string, unknown>) => {
    const res_1 = await fetch(`/api/user/profile`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: "include",
      body: JSON.stringify(body)
    });
    const data_0 = await res_1.json();
    if (data_0.success) invalidateCache("/api/user");
    return data_0;
  };
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      const data_1 = await saveProfile({
        name,
        phone,
        avatar_url: avatarUrl || undefined,
        bio: bio.trim() || null
      });
      if (data_1.success) toast.success("Profil berhasil diperbarui");else toast.error(data_1.error || "Gagal memperbarui profil");
    } catch {
      toast.error("Terjadi kesalahan. Coba lagi.");
    } finally {
      setProfileLoading(false);
    }
  };
  const handleChangePassword = async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    setPasswordLoading(true);
    try {
      const res_2 = await fetch(`/api/user/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      const data_2 = await res_2.json();
      if (data_2.success) {
        toast.success("Password berhasil diubah");
        setCurrentPassword("");
        setNewPassword("");
      } else {
        toast.error(data_2.error || "Gagal mengubah password");
      }
    } catch {
      toast.error("Terjadi kesalahan. Coba lagi.");
    } finally {
      setPasswordLoading(false);
    }
  };
  return <div className="max-w-xl space-y-5">
      <PageHeader eyebrow="Akun" title="Pengaturan Akun" description="Kelola informasi profil dan keamanan akun" />

      {/* Membership Info Card */}
      <Card className="overflow-hidden border border-black/[0.06] dark:border-white/[0.05]">
        <CardHeader className="bg-[color-mix(in_srgb,var(--brand-primary)_7%,transparent)] dark:bg-[color-mix(in_srgb,var(--brand-primary-bright)_7%,transparent)] border-b border-black/[0.04] dark:border-white/[0.05] py-4">
          <div className="flex items-center gap-2">
            <Star className="h-5 w-5 text-[var(--brand-primary)] dark:text-[var(--brand-primary-bright)] fill-[var(--brand-primary)]/20 dark:fill-[var(--brand-primary-bright)]/20" />
            <CardTitle className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">Informasi Layanan</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-5">
          {initialLoading ? <div className="flex flex-col gap-2">
              <div className="skeleton h-4 w-1/3 rounded" />
              <div className="skeleton h-3 w-1/2 rounded" />
            </div> : membership ? <div>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-1.5 leading-snug mb-1">
                    {membership.plan_name}
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
                      Aktif
                    </span>
                  </h3>
                  <div className="flex flex-col gap-1.5 mt-3 text-xs text-gray-600 dark:text-slate-400">
                    <div className="flex items-center gap-2">
                      <Zap className="h-3.5 w-3.5 text-amber-500" />
                      <span>Tipe Tagihan: <span className="font-semibold capitalize">{membership.duration_type}</span></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5 text-blue-500" />
                      <span>
                        {membership.expires_at ? <>Berakhir pada <span className="font-semibold">{formatDate(membership.expires_at)}</span></> : <span className="font-semibold">Akses Lifetime / Selamanya</span>}
                      </span>
                    </div>
                  </div>
                </div>
                {membership.expires_at && <Button onClick={() => window.open("/premium/", "_blank")} variant="secondary" size="sm" className="border-[var(--brand-primary)] dark:border-[var(--brand-primary-bright)] text-[var(--brand-primary)] dark:text-[var(--brand-primary-bright)] hover:bg-[var(--brand-primary-soft)] dark:hover:bg-[var(--brand-primary-bright)]/10 shrink-0 active:scale-[0.98]">
                    Perpanjang Akses
                  </Button>}
              </div>

              {/* Expiry Warning */}
              {(() => {
            if (!membership.expires_at) return null;
            const daysLeft = Math.ceil((new Date(membership.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
            if (daysLeft > 0 && daysLeft <= 7) {
              return <div className="mt-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 flex items-start gap-2.5 text-xs text-amber-800 dark:text-amber-400">
                      <ShieldAlert className="h-4 w-4 text-amber-600 shrink-0 mt-0.5 animate-pulse" />
                      <div>
                        <p className="font-semibold">Membership kamu hampir berakhir!</p>
                        <p className="mt-0.5 opacity-90">Akses all-class kamu akan dinonaktifkan dalam {daysLeft} hari. Perbarui langganan kamu sekarang untuk terus belajar tanpa gangguan.</p>
                      </div>
                    </div>;
            }
            return null;
          })()}
            </div> : monetizationMode === "digital_product" ?
        // Mode produk digital: tanpa upsell membership — kartu status netral, copy produk.
        <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-1.5 leading-snug">
                Akun Aktif
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
                  Aktif
                </span>
              </h3>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 max-w-sm">
                Akses produk digitalmu mengikuti yang sudah kamu beli. Produk yang sudah dibeli bisa
                dibuka kapan saja dari menu Produk Saya.
              </p>
            </div> : monetizationMode === "per_class" ?
        // Mode per kelas: tanpa upsell membership — kartu status netral.
        <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-1.5 leading-snug">
                Akun Aktif
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20">
                  Aktif
                </span>
              </h3>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 max-w-sm">
                Akses belajarmu mengikuti kelas yang kamu beli. Kelas yang sudah dibeli bisa dibuka
                kapan saja dari menu Kelas Saya.
              </p>
            </div> : <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-1.5 leading-snug">
                  Akses Terbatas (Free Plan)
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-white/10">
                    Free
                  </span>
                </h3>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 max-w-sm">
                  Dapatkan akses ke semua materi premium, sertifikasi resmi, dan komunitas member Pro.
                </p>
              </div>
              <Button onClick={() => window.open("/premium/", "_blank")} className="bg-[var(--brand-primary)] dark:bg-[var(--brand-primary-bright)] hover:bg-[var(--brand-primary-hover)] dark:hover:bg-[var(--brand-primary-hover)] text-[var(--brand-on-primary,#fff)] shrink-0 active:scale-[0.98]" size="sm">
                Upgrade ke Pro
              </Button>
            </div>}
        </CardContent>
      </Card>

      {/* Profile Form */}
      <Card>
        <CardHeader>
          <CardTitle>Informasi Profil</CardTitle>
        </CardHeader>
        <CardContent>
          {initialLoading ? <div className="flex flex-col gap-4">
              {[1, 2, 3].map(i => <div key={i}>
                  <div className="skeleton h-3 w-24 mb-2 rounded" />
                  <div className="skeleton h-10 w-full rounded-lg" />
                </div>)}
              <div className="skeleton h-9 w-32 rounded-lg" />
            </div> : <form onSubmit={handleUpdateProfile} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-[#6B7280] dark:text-slate-400 uppercase tracking-[0.06em]">Avatar Profile</label>
                <div className="p-4 rounded-xl border border-black/[0.06] dark:border-white/[0.05] bg-gray-50/50 dark:bg-white/[0.02]">
                  <DicebearCustomizer initialUrl={avatarUrl} defaultSeed={user?.name || "member"} onSelect={url => setAvatarUrl(url)} />
                </div>
              </div>
              <div className="flex flex-col gap-1.5 mt-2">
                <label className="text-xs font-semibold text-[#6B7280] dark:text-slate-400 uppercase tracking-[0.06em]">Email</label>
                <input value={user?.email || ""} disabled className="w-full px-3.5 py-2.5 text-sm rounded-xl border border-black/[0.06] dark:border-white/[0.06] text-[#9CA3AF] dark:text-slate-500 cursor-not-allowed opacity-80" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-[#6B7280] dark:text-slate-400 uppercase tracking-[0.06em]">Nama Lengkap</label>
                <input value={name} onChange={e_1 => setName(e_1.target.value)} required className={inputClass} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-[#6B7280] dark:text-slate-400 uppercase tracking-[0.06em]">
                  Nomor HP{" "}
                  <span className="text-[#9CA3AF] dark:text-slate-500 font-normal normal-case tracking-normal">(opsional)</span>
                </label>
                <input value={phone} onChange={e_2 => setPhone(e_2.target.value)} placeholder="+62..." className={inputClass} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-[#6B7280] dark:text-slate-400 uppercase tracking-[0.06em]">
                  Deskripsi Tentang Saya{" "}
                  <span className="text-[#9CA3AF] dark:text-slate-500 font-normal normal-case tracking-normal">(opsional)</span>
                </label>
                <textarea value={bio} onChange={e_3 => setBio(e_3.target.value)} maxLength={500} rows={3} placeholder="Ceritakan tentang dirimu…" className={`${inputClass} resize-none`} />
                <p className="text-[11px] text-[#9CA3AF] dark:text-slate-500 text-right">{bio.length}/500</p>
              </div>
              <Button type="submit" className="self-start" loading={profileLoading}>
                Simpan Perubahan
              </Button>
            </form>}
        </CardContent>
      </Card>


      {/* Password Form */}
      <Card>
        <CardHeader>
          <CardTitle>Ubah Password</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="current-password" className="text-xs font-semibold text-[#6B7280] dark:text-slate-400 uppercase tracking-[0.06em]">
                Password Saat Ini <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input id="current-password" type={showCurrentPassword ? "text" : "password"} value={currentPassword} onChange={e_4 => setCurrentPassword(e_4.target.value)} required autoComplete="current-password" className={`${inputClass} pr-11`} />
                <button type="button" onClick={() => setShowCurrentPassword(v => !v)} aria-label={showCurrentPassword ? "Sembunyikan password" : "Tampilkan password"} className="absolute right-1 top-1/2 -translate-y-1/2 p-2.5 text-[#6B7280] dark:text-slate-400 hover:text-[#0D0D0D] dark:hover:text-white transition-colors">
                  {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="new-password" className="text-xs font-semibold text-[#6B7280] dark:text-slate-400 uppercase tracking-[0.06em]">
                Password Baru <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input id="new-password" type={showNewPassword ? "text" : "password"} value={newPassword} onChange={e_5 => setNewPassword(e_5.target.value)} required minLength={8} autoComplete="new-password" placeholder="Min. 8 karakter" className={`${inputClass} pr-11`} />
                <button type="button" onClick={() => setShowNewPassword(v_0 => !v_0)} aria-label={showNewPassword ? "Sembunyikan password" : "Tampilkan password"} className="absolute right-1 top-1/2 -translate-y-1/2 p-2.5 text-[#6B7280] dark:text-slate-400 hover:text-[#0D0D0D] dark:hover:text-white transition-colors">
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <Button type="submit" className="self-start bg-[#0D0D0D] dark:bg-white hover:bg-[#1a1a1a] dark:hover:bg-gray-200 dark:text-black" loading={passwordLoading}>
              Ubah Password
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Sesi & perangkat aktif */}
      <SessionManager />
    </div>;
}